import { createContext, useContext, ReactNode } from 'react';
import { useWorkflowMetadataByTrackingReference } from './useWorkflowMetadataByTrackingReference';
import { useWorkflowMetadataByCustomerId } from './useWorkflowMetadataByCustomerId';

/**
 * Explicit metadata source type - Application MUST choose, no arbitrary priority rules.
 * 
 * - 'tracking-reference': User arrived via signup email link
 * - 'customer-id': Authenticated user creating identity from dashboard
 * - 'none': No context available (edge case - design issue)
 */
export type MetadataSource =
  | { type: 'tracking-reference'; trackingReference: string }
  | { type: 'customer-id'; customerId: string }
  | { type: 'none' };

/**
 * Workflow metadata state provided to all wizard components.
 * 
 * - status: Current loading state. `not-found` is a request that succeeded but gave no provider to
 *   read, told apart from `error` (the request failed) and from `loaded` (a provider was read).
 *   Per source: the tracking-reference lookup reports it for no item, or for an item of an
 *   unsupported association type; the customer-id lookup reports it for an empty list only
 * - source: Which source type was used to fetch metadata
 * - organisationName: Display name of the organization
 * - logoUrl: URL of the organization's logo
 * - redirectUrl: URL the invite sends the customer back to after completion, which the end screen
 *   offers as a button and never redirects to. Resolves to the dynamic per-invite callback URL when
 *   present, otherwise the organisation-code return URL.
 */
export type WorkflowMetadataState = {
  status: 'loading' | 'loaded' | 'error' | 'not-found';
  source: 'tracking-reference' | 'customer-id' | 'none';
  organisationName?: string;
  logoUrl?: string;
  redirectUrl?: string;
};

/**
 * The `WorkflowMetadataState` status for the outcome of one of the metadata lookup hooks. Loading
 * wins, then a failed request, then a request that found nothing to read.
 */
export function resolveWorkflowMetadataStatus({
  isLoading,
  error,
  notFound,
}: {
  isLoading: boolean;
  error: boolean;
  notFound: boolean;
}): WorkflowMetadataState['status'] {
  if (isLoading) {
    return 'loading';
  }
  if (error) {
    return 'error';
  }
  return notFound ? 'not-found' : 'loaded';
}

const WorkflowMetadataContext = createContext<WorkflowMetadataState | undefined>(undefined);

interface WorkflowMetadataProviderProps {
  source: MetadataSource;
  children: ReactNode;
}

/**
 * Provider that fetches workflow metadata once based on explicit source type.
 * 
 * This component centralizes metadata fetching for the entire wizard flow,
 * ensuring metadata is fetched only once and shared across all wizard steps.
 * 
 * @param source - Explicit metadata source (tracking-reference, customer-id, or none)
 * @param children - Wizard components that will consume the metadata
 * 
 * @example
 * ```tsx
 * const source: MetadataSource = trackId
 *   ? { type: 'tracking-reference', trackingReference: trackId }
 *   : customerId
 *     ? { type: 'customer-id', customerId }
 *     : { type: 'none' };
 * 
 * <WorkflowMetadataProvider source={source}>
 *   <WizardForm />
 * </WorkflowMetadataProvider>
 * ```
 */
export function WorkflowMetadataProvider({ source, children }: WorkflowMetadataProviderProps) {
  // Use internal hooks based on source type
  const trackingReferenceResult = useWorkflowMetadataByTrackingReference(
    source.type === 'tracking-reference' ? source.trackingReference : null,
  );
  
  const customerIdResult = useWorkflowMetadataByCustomerId(
    source.type === 'customer-id' ? source.customerId : null,
  );

  // Determine state based on source type
  let state: WorkflowMetadataState;

  if (source.type === 'tracking-reference') {
    const { metadata, organisationName, isLoading, error, notFound } = trackingReferenceResult;
    state = {
      status: resolveWorkflowMetadataStatus({ isLoading, error, notFound }),
      source: 'tracking-reference',
      organisationName,
      logoUrl: metadata?.logoUrl ?? undefined,
      redirectUrl: metadata?.redirectUrl ?? undefined,
    };
  } else if (source.type === 'customer-id') {
    const { metadata, organisationName, isLoading, error, notFound } = customerIdResult;
    state = {
      status: resolveWorkflowMetadataStatus({ isLoading, error, notFound }),
      source: 'customer-id',
      organisationName,
      logoUrl: metadata?.logoUrl ?? undefined,
      redirectUrl: metadata?.redirectUrl ?? undefined,
    };
  } else {
    // 'none' case - no metadata to fetch
    state = {
      status: 'loaded',
      source: 'none',
    };
  }

  return (
    <WorkflowMetadataContext.Provider value={state}>
      {children}
    </WorkflowMetadataContext.Provider>
  );
}

/**
 * Hook to access workflow metadata from any wizard component.
 * 
 * Must be used within a WorkflowMetadataProvider.
 * 
 * @returns Current workflow metadata state
 * @throws Error if used outside WorkflowMetadataProvider
 * 
 * @example
 * ```tsx
 * function WizardStep() {
 *   const { status, organisationName, logoUrl } = useWorkflowMetadata();
 *   
 *   if (status === 'loading') return <Loading />;
 *   
 *   return (
 *     <div>
 *       {organisationName && <CompanyLogoAndName companyName={organisationName} companyLogoUrl={logoUrl} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useWorkflowMetadata(): WorkflowMetadataState {
  const context = useContext(WorkflowMetadataContext);
  
  if (context === undefined) {
    throw new Error('useWorkflowMetadata must be used within a WorkflowMetadataProvider');
  }
  
  return context;
}
